/**
 * Boiler/Reactor — adds heat to the throughflow.
 *
 * Two modes:
 *   T_out/P_out mode — computes the outlet state directly and returns
 *     deltaH = h(P_out, T_out) − h_in (applied to the outlet cell).
 *   Q_rate mode — returns the TOTAL heat rate (W) on `energySource`.
 *     N1: the network converts it at apply time (Δh = Q̇/ṁ_outflow) —
 *     this channel is LIVE, no longer a silent no-op. See the
 *     ComponentUpdate contract in types.ts.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { propsFromPT_SI } from '../core/fluidAdapter';

export class NSBoiler implements NSComponent {
  id: string;
  type = 'boiler';
  params: Record<string, any>;

  constructor(id: string, params: { T_out?: number; P_out?: number; Q_rate?: number }) {
    this.id = id;
    this.params = { T_out: 553.15, P_out: 6.4e6, ...params }; // 280°C, 6.4 MPa default
  }

  applyBoundaryConditions(inlet: Junction, _outlet: Junction, _dt: number): ComponentUpdate {
    const { T_out, P_out, Q_rate } = this.params;

    if (Q_rate != null) {
      // Specified heat-rate mode: the network consumes energySource as
      // Δh = Q̇/ṁ_outflow at apply time (N1) and records the APPLIED duty
      // on params._appliedQ_W for the UI sync.
      return {
        deltaP: 0,
        deltaH: 0,
        momentumSource: 0,
        energySource: Q_rate, // W
        metadata: { Q_rate },
      };
    }

    // Specified outlet conditions mode
    try {
      const props_out = propsFromPT_SI(P_out, T_out);
      const deltaH = props_out.h - inlet.h;

      return {
        deltaP: P_out - inlet.P,
        deltaH,
        momentumSource: 0,
        energySource: 0,
        metadata: { Q_boiler: deltaH, T_out: props_out.T, P_out },
      };
    } catch {
      return { deltaP: 0, deltaH: 0, momentumSource: 0, energySource: 0, metadata: {} };
    }
  }
}
